import { onEdit } from "apps/web/src/components/MenuItem/Dialog/utils";
import { useSaveMenuItemAllergens } from "apps/web/src/components/MenuItem/Dialog/useSaveMenuItemAllergens";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import LoadingBackdrop from "apps/web/src/components/universal/LoadingBackdrop";
import {
  useEditReusableMenuItemMutation,
  useEditReusableMenuItemPartialMutation,
  useMenuItemDialogQuery,
} from "apps/web/src/types";
import { MenuItemEditDialogContent } from "apps/web/src/views/MenuItems/Dialog/Edit/MenuItemEditContent";
import { OnSaveMenuItemArgs } from "apps/web/src/views/MenuItems/Dialog/Edit/MenuItemEditDialog";
import { useNavigate, useParams } from "react-router-dom-v5-compat";
import { getOrgMenuItemNavigation, NAV_ORG_KITCHEN_MENU_ITEMS } from "./KitchenPaths";

const MenuItemEditPage = () => {
  const { id: menuItemId } = useParams<{ id: string }>();
  const { setMessage } = useSnackbar();
  const navigate = useNavigate();

  const onClose = () => {
    navigate(NAV_ORG_KITCHEN_MENU_ITEMS);
  };

  const { data, loading } = useMenuItemDialogQuery({
    variables: { menuItemId: menuItemId ?? "" },
    skip: !menuItemId,
    fetchPolicy: "no-cache",
  });

  const [editReusableMenuItem, { loading: savingEdit }] = useEditReusableMenuItemMutation({
    onError: () => setMessage("error", "Unable to edit menu item"),
    onCompleted: () => {
      setMessage("success", "Edited menu item");
      navigate(NAV_ORG_KITCHEN_MENU_ITEMS);
    },
  });

  const [editReusableMenuItemPartial, { loading: savingPartial }] = useEditReusableMenuItemPartialMutation({
    onError: () => setMessage("error", "Unable to edit menu item"),
    onCompleted: () => {
      setMessage("success", "Edited menu item");
      navigate(NAV_ORG_KITCHEN_MENU_ITEMS);
    },
  });

  const saveAllergens = useSaveMenuItemAllergens();

  const handleSave = (state: OnSaveMenuItemArgs["final"]) => {
    if (!data) {
      return;
    }
    onEdit({
      editReusableMenuItem,
      editReusableMenuItemPartial,
      saveAllergens,
      state: { initial: data.currentMenuItem, final: state },
    });
  };

  const handleDuplicate = () => {
    if (!menuItemId) {
      return;
    }
    const { to, options } = getOrgMenuItemNavigation({ duplicatedMenuItemId: menuItemId, isDuplicate: true });
    navigate(to, { ...options, replace: true });
  };

  const isLoading = !menuItemId || loading || !data || savingEdit || savingPartial;

  return isLoading ? (
    <LoadingBackdrop open={isLoading} onClose={onClose} />
  ) : (
    <MenuItemEditDialogContent
      open
      pageView
      onClose={onClose}
      onSave={handleSave}
      menuItem={data.currentMenuItem}
      onDuplicate={handleDuplicate}
    />
  );
};

export default MenuItemEditPage;
